import React, { useState, useRef, useEffect, useMemo } from "react";
import styled from "styled-components";

const DropdownContainer = styled.div`
  position: relative;
  width: 100%;
`;

const DropdownHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 10px;
  border: 1.5px solid ${({ $isOpen, $hasSelection }) =>
    $isOpen ? "#3f88a5" : $hasSelection ? "#3f88a5" : "#e2e8f0"};
  border-radius: 8px;
  background: #fff;
  cursor: ${({ $disabled }) => ($disabled ? "not-allowed" : "pointer")};
  min-height: 34px;
  font-size: 13px;
  color: ${({ $hasSelection }) => ($hasSelection ? "#1e293b" : "#94a3b8")};
  transition: all 0.2s;
  opacity: ${({ $disabled }) => ($disabled ? 0.7 : 1)};

  &:hover {
    border-color: ${({ $disabled }) => ($disabled ? "#e2e8f0" : "#3f88a5")};
  }
`;

const HeaderText = styled.div`
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Chevron = styled.div`
  margin-left: 8px;
  font-size: 10px;
  color: #64748b;
  transform: ${({ $isOpen }) => ($isOpen ? "rotate(180deg)" : "rotate(0deg)")};
  transition: transform 0.2s;
`;

const DropdownBody = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  z-index: 999;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
  max-height: 250px;
  display: flex;
  flex-direction: column;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 8px 12px;
  border: none;
  border-bottom: 1px solid #e2e8f0;
  outline: none;
  font-size: 13px;
  background: #f8fafc;
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;

  &::placeholder {
    color: #94a3b8;
  }
`;

const OptionsList = styled.div`
  overflow-y: auto;
  padding: 4px 0;
  flex: 1;
`;

const OptionItem = styled.div`
  display: flex;
  align-items: center;
  padding: 6px 12px;
  cursor: pointer;
  font-size: 13px;
  color: ${({ $isSelected }) => ($isSelected ? "#fff" : "#1e293b")};
  background: ${({ $isSelected }) => ($isSelected ? "#3f88a5" : "transparent")};

  &:hover {
    background: ${({ $isSelected }) => ($isSelected ? "#3f88a5" : "#f1f5f9")};
  }
`;

const SearchableDropdown = ({
  options = [],
  value,
  onChange,
  placeholder = "Select an option...",
  searchPlaceholder = "Search...",
  disabled = false,
  dropDirection = "down",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const containerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        setIsOpen(false);
        setSearchTerm("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const filteredOptions = useMemo(() => {
    return options.filter((opt) => {
      const label = typeof opt === "object" ? opt.label : String(opt);
      return label.toLowerCase().includes(searchTerm.toLowerCase());
    });
  }, [options, searchTerm]);

  const handleToggle = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
      setSearchTerm("");
    }
  };

  const handleSelectOption = (val) => {
    onChange(val);
    setIsOpen(false);
    setSearchTerm("");
  };

  const getHeaderText = () => {
    if (value === undefined || value === null || value === "") return placeholder;
    const selectedOpt = options.find(
      (opt) => (typeof opt === "object" ? opt.value : opt) === value
    );
    if (!selectedOpt) return placeholder;
    return typeof selectedOpt === "object" ? selectedOpt.label : selectedOpt;
  };

  const bodyStyle = dropDirection === "up"
    ? { bottom: "calc(100% + 4px)", top: "auto" }
    : { top: "calc(100% + 4px)", bottom: "auto" };

  return (
    <DropdownContainer ref={containerRef}>
      <DropdownHeader
        onClick={handleToggle}
        $isOpen={isOpen}
        $hasSelection={value !== undefined && value !== null && value !== ""}
        $disabled={disabled}
      >
        <HeaderText>{getHeaderText()}</HeaderText>
        <Chevron $isOpen={isOpen}>▼</Chevron>
      </DropdownHeader>

      {isOpen && (
        <DropdownBody style={bodyStyle}>
          <SearchInput
            type="text"
            placeholder={searchPlaceholder}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            autoFocus
          />
          <OptionsList>
            {filteredOptions.length > 0 ? (
              filteredOptions.map((opt) => {
                const val = typeof opt === "object" ? opt.value : opt;
                const label = typeof opt === "object" ? opt.label : opt;
                const isSelected = val === value;
                return (
                  <OptionItem
                    key={val}
                    $isSelected={isSelected}
                    onClick={() => handleSelectOption(val)}
                  >
                    {label}
                  </OptionItem>
                );
              })
            ) : (
              <div style={{ padding: "8px 12px", fontSize: 12, color: "#94a3b8", textAlign: "center" }}>
                No options found
              </div>
            )}
          </OptionsList>
        </DropdownBody>
      )}
    </DropdownContainer>
  );
};

export default SearchableDropdown;
